import type {
  DialerReportSummaryItem,
  IncomingReportTeamRow,
  OutgoingReportTeamRow,
  ReportTeamRow,
} from '../types';
import type { CallDirectionFilter } from '../components/reports-sub-header';
import { TeamData, UserCallData } from '../../../../../types';
import { formatDuration } from '../../../utils/helpers';

type MetricPayload = Record<
  string,
  { value: number; trend: number; percentage?: string }
>;

const getValue = (m: { value?: number } | undefined): number => m?.value ?? 0;

const getPercentage = (m: { percentage?: string } | undefined): number =>
  parseFloat(String(m?.percentage ?? '0').replace('%', '')) || 0;

/**
 * Maps UserCallData to flat row (incoming or outgoing based on callDirection).
 */
export function mapUserCallDataToRow(
  user: UserCallData,
  callDirection: CallDirectionFilter,
): ReportTeamRow {
  if (callDirection === 'incoming') {
    return {
      id: String(user.id ?? `${user.firstName ?? ''}-${user.lastName ?? ''}`),
      teamName:
        [user.firstName, user.lastName].filter(Boolean).join(' ') || '-',
      totalCallsReceived: getValue(user.totalCallsReceived),
      callsForwarded: getValue(user.forwardedIncomingCallCount),
      callsPicked: getValue(user.callsPicked),
      callsPickedPercent: getPercentage(user.callsPicked),
      callsUnanswered: getValue(user.callsUnanswered),
      callsUnansweredPercent: getPercentage(user.callsUnanswered),
      totalTalkTimeMins: getValue(user.totalTalkTime),
      avgTalkTimeMins: getValue(user.avgTalkTime),
      directIncomingCalls: getValue(user.directIncomingCallCount),
      forwardedIncomingCalls: getValue(user.forwardedIncomingCallCount),
    } as IncomingReportTeamRow;
  }
  return {
    id: String(user.id ?? `${user.firstName ?? ''}-${user.lastName ?? ''}`),
    teamName: [user.firstName, user.lastName].filter(Boolean).join(' ') || '-',
    totalCallsMade: getValue(user.totalCallsMade),
    callsConnected: getValue(user.callsConnected),
    callsConnectedPercent: getPercentage(user.callsConnected),
    callsUnanswered: getValue(user.callsUnanswered),
    callsUnansweredPercent: getPercentage(user.callsUnanswered),
    totalTalkTimeMins: getValue(user.totalTalkTime),
    avgTalkTimeMins: getValue(user.avgTalkTime),
    avgRingDurationSecs: getValue(user.avgRingDuration),
  } as OutgoingReportTeamRow;
}

/**
 * Maps TeamData to accordion row (parent + subRows from users).
 */
/**
 * Sub-rows must have stable unique ids within the table. TanStack expanded state
 * and React keys use row.id — duplicate ids (common when user.id is missing or
 * repeated) cause collapsed rows to stay visible and sub-rows to appear to multiply.
 */
function withUniqueSubRowIds<T extends ReportTeamRow>(
  parentRowId: string,
  subRows: T[],
): T[] {
  return subRows.map((row, index) => ({
    ...row,
    id: `${parentRowId}__sub__${index}__${row.id}`,
  }));
}

export function mapTeamDataToAccordionRow(
  team: TeamData,
  callDirection: CallDirectionFilter,
): ReportTeamRow {
  const parentRowId = String(team.id ?? team.name);
  const s = team.stats;
  if (callDirection === 'incoming') {
    const subRows = (team.users ?? []).map((u) =>
      mapUserCallDataToRow(u, callDirection),
    ) as IncomingReportTeamRow[];
    return {
      id: parentRowId,
      teamName: team.name,
      totalCallsReceived: getValue(s.totalCallsReceived),
      callsForwarded: getValue(s.forwardedIncomingCallCount),
      callsPicked: getValue(s.callsPicked),
      callsPickedPercent: getPercentage(s.callsPicked),
      callsUnanswered: getValue(s.callsUnanswered),
      callsUnansweredPercent: getPercentage(s.callsUnanswered),
      totalTalkTimeMins: getValue(s.totalTalkTime),
      avgTalkTimeMins: getValue(s.avgTalkTime),
      directIncomingCalls: getValue(s.directIncomingCallCount),
      forwardedIncomingCalls: getValue(s.forwardedIncomingCallCount),
      subRows: withUniqueSubRowIds(parentRowId, subRows),
    } as IncomingReportTeamRow;
  }
  const subRows = (team.users ?? []).map((u) =>
    mapUserCallDataToRow(u, callDirection),
  ) as OutgoingReportTeamRow[];
  return {
    id: parentRowId,
    teamName: team.name,
    totalCallsMade: getValue(s.totalCallsMade),
    callsConnected: getValue(s.callsConnected),
    callsConnectedPercent: getPercentage(s.callsConnected),
    callsUnanswered: getValue(s.callsUnanswered),
    callsUnansweredPercent: getPercentage(s.callsUnanswered),
    totalTalkTimeMins: getValue(s.totalTalkTime),
    avgTalkTimeMins: getValue(s.avgTalkTime),
    avgRingDurationSecs: getValue(s.avgRingDuration),
    subRows: withUniqueSubRowIds(parentRowId, subRows),
  } as OutgoingReportTeamRow;
}

/**
 * Maps ReportsStats API payload to DialerReportSummaryItem for summary cards.
 */
export function mapReportsStatsToSummaryItems(
  payload: MetricPayload | undefined,
  callDirection: CallDirectionFilter,
): DialerReportSummaryItem[] {
  // const { minutes, seconds } = getDurationParts(payload?.);
  if (!payload) return [];

  if (callDirection === 'incoming') {
    return [
      {
        label: 'Total Calls Received',
        key: 'totalCallReceived',
        value: payload.totalCallReceived?.value ?? 0,
        trend: payload.totalCallReceived?.trend ?? 0,
      },
      {
        label: 'Calls Picked',
        key: 'callsPicked',
        value: payload.callsPicked?.value ?? 0,
        percentage: payload.callsPicked?.percentage,
        trend: payload.callsPicked?.trend ?? 0,
      },
      {
        label: 'Calls Unanswered',
        key: 'callsUnanswered',
        value: payload.callsUnanswered?.value ?? 0,
        percentage: payload.callsUnanswered?.percentage,
        trend: payload.callsUnanswered?.trend ?? 0,
      },
      {
        label: 'Total Talk Time',
        key: 'totalTalkTime',
        value: formatDuration(Math.floor(payload?.totalTalkTime?.value ?? 0)),
        trend: payload.totalTalkTime?.trend ?? 0,
      },
      {
        label: 'Avg Talk Time',
        key: 'avgTalkTime',
        value: formatDuration(Math.floor(payload?.avgTalkTime?.value ?? 0)),
        trend: payload.avgTalkTime?.trend ?? 0,
      },
    ];
  }

  return [
    {
      label: 'Total Calls Made',
      key: 'callsMade',
      value: payload.totalCallsMade?.value ?? payload.callsMade?.value ?? 0,
      trend: payload.totalCallsMade?.trend ?? payload.callsMade?.trend ?? 0,
    },
    {
      label: 'Calls Connected',
      key: 'callsConnected',
      value: payload.callsConnected?.value ?? 0,
      percentage: payload.callsConnected?.percentage,
      trend: payload.callsConnected?.trend ?? 0,
    },
    {
      label: 'Calls Unanswered',
      key: 'callsUnanswered',
      value: payload.callsUnanswered?.value ?? 0,
      percentage: payload.callsUnanswered?.percentage,
      trend: payload.callsUnanswered?.trend ?? 0,
    },
    {
      label: 'Voicemail Dropped',
      key: 'voiceMailDropped',
      value: payload.voiceMailDropped?.value ?? 0,
      percentage: payload.voiceMailDropped?.percentage,
      trend: payload.voiceMailDropped?.trend ?? 0,
    },
    {
      label: 'Total Talk time',
      key: 'totalTalktime',
      value: formatDuration(Math.floor(payload.totalTalkTime?.value ?? 0)),
      trend: payload.totalTalkTime?.trend ?? 0,
    },
    {
      label: 'Avg Talk time',
      key: 'avgTalktime',
      value: formatDuration(Math.floor(payload?.avgTalkTime?.value ?? 0)),
      trend: payload.avgTalkTime?.trend ?? 0,
    },
    {
      label: 'Avg Ring Duration',
      key: 'avgRingDuration',
      value:
        payload?.avgRingDuration?.value >= 60
          ? formatDuration(Math.floor(payload?.avgRingDuration?.value) ?? 0)
          : Math.floor(payload?.avgRingDuration?.value),
      valueSuffix: payload?.avgRingDuration?.value < 60 && 's',
      trend: payload?.avgRingDuration?.trend ?? 0,
    },
  ];
}
